import { useState, useEffect } from 'react';
import {
  Input,
  Button,
  Card,
  Alert,
  Typography,
  Space,
  Spin,
  Flex,
} from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useAutoPreenchimento } from '@/hooks/useAutoPreenchimento';
import { unifiedClienteService } from '@/services/unifiedService';
import { useAuth } from '@/features/auth';
import { useProfileSwitcher } from '@/hooks/useProfileSwitcher';
import { supabase } from '@/integrations/supabase/client';

const { Text } = Typography;

interface Cliente {
  id: number;
  nome: string;
  cpf: string;
  email: string;
  tel1: string;
}

interface ClienteSearchUnifiedProps {
  onClienteSelect: (cliente: Cliente) => void;
  selectedCliente?: Cliente | null;
  placeholder?: string;
}

export function ClienteSearchUnified({
  onClienteSelect,
  selectedCliente,
  placeholder,
}: ClienteSearchUnifiedProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Cliente[]>([]);
  const [showResults, setShowResults] = useState(false);

  const { profile } = useAuth();
  const { getCurrentProfile } = useProfileSwitcher();

  const {
    loading: loadingAutoPreenchimento,
    dadosCliente,
    erro: erroAutoPreenchimento,
    buscarPorCPF,
    formatarCPF,
    limparDados
  } = useAutoPreenchimento();

  // Placeholder inteligente
  const dynamicPlaceholder = placeholder || 'Digite nome, CPF, CNPJ, email ou telefone';

  // Detectar tipo de busca baseado no termo
  const detectarTipoBusca = (term: string): string => {
    const termoLimpo = term.replace(/\D/g, '');

    // CPF (11 dígitos)
    if (termoLimpo.length === 11) return 'cpf';

    // CNPJ (14 dígitos)
    if (termoLimpo.length === 14) return 'cnpj';

    // Email (contém @)
    if (term.includes('@')) return 'email';

    // Telefone (contém apenas números e tem formato de telefone)
    if (/^\d{10,11}$/.test(termoLimpo)) return 'telefone';

    // Nome (padrão)
    return 'nome';
  };

  // Buscar clientes com busca inteligente
  const searchClientes = async (term: string) => {
    if (!term || term.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const tipoBusca = detectarTipoBusca(term);

      // Para CPF completo, tentar busca específica primeiro
      if (tipoBusca === 'cpf') {
        const cpfLimpo = term.replace(/\D/g, '');
        try {
          const response = await unifiedClienteService.findByCpf(cpfLimpo);
          if (response && response.id) {
            const cliente: Cliente = {
              id: response.id,
              nome: response.nome,
              cpf: response.cpf,
              email: response.email || '',
              tel1: response.tel1 || ''
            };
            setSearchResults([cliente]);
            setIsSearching(false);
            return;
          }
        } catch (error) {
          // CPF não encontrado, continuar com busca geral
          console.log('CPF não encontrado, fazendo busca geral');
        }
      }

      // Busca geral para todos os tipos
      const currentRole = getCurrentProfile;
      let filterByUser: string | undefined;

      // Para líderes de vendas, buscar clientes criados pelos relacionadores da mesma equipe
      if (currentRole === 'LIDER_VENDAS') {
        try {
          // Buscar o supervisor do líder atual (líder tem tipo 'lider', não 'relacionador')
          const { data: consultorAtual } = await supabase
            .from('consultores')
            .select('supervisor_id')
            .eq('user_id', profile?.uuid)
            .eq('tipo', 'lider')
            .single();

          if (consultorAtual?.supervisor_id) {
            // Buscar todos os relacionadores supervisionados pelo mesmo supervisor
            const { data: relacionadores } = await supabase
              .from('consultores')
              .select('user_id')
              .eq('supervisor_id', consultorAtual.supervisor_id)
              .eq('tipo', 'relacionador')
              .eq('status', 'ativo');

            if (relacionadores && relacionadores.length > 0) {
              // Filtrar por múltiplos usuários (relacionadores da equipe + o próprio líder)
              const userIds = relacionadores.map(r => r.user_id);
              // Incluir o próprio líder de vendas na lista
              if (profile?.uuid && !userIds.includes(profile.uuid)) {
                userIds.push(profile.uuid);
              }
              filterByUser = userIds.join(',');
            } else {
              // Se não há relacionadores, mostrar apenas clientes do próprio líder
              filterByUser = profile?.uuid;
            }
          } else {
            // Fallback para o próprio usuário
            filterByUser = profile?.uuid;
          }
        } catch (error) {
          console.error('Erro ao buscar relacionadores da equipe:', error);
          filterByUser = profile?.uuid; // Fallback
        }
      } else if (currentRole === 'VENDEDOR') {
        // Vendedores comuns só veem seus próprios clientes
        filterByUser = profile?.uuid;
      }
      // Outros perfis (relacionadores, supervisores, etc.) veem todos os clientes

      const response = await unifiedClienteService.listarClientes(1, 100, term, profile?.tenant_id?.toString(), filterByUser);

      const clientes = response.data || response || [];

      // Converter para o formato esperado
      const clientesFormatados: Cliente[] = clientes.map((cliente: any) => ({
        id: cliente.id,
        nome: cliente.nome,
        cpf: cliente.cpf,
        email: cliente.email || '',
        tel1: cliente.tel1 || ''
      }));

      // Filtrar resultados baseado no tipo detectado
      const filteredResults = clientesFormatados.filter(cliente => {
        const searchValue = term.toLowerCase().trim();

        switch (tipoBusca) {
          case 'cpf':
          case 'cnpj':
            // Para documentos, comparar apenas os números
            const cpfLimpo = cliente.cpf ? cliente.cpf.replace(/\D/g, '') : '';
            const termLimpo = term.replace(/\D/g, '');
            return cpfLimpo && cpfLimpo.includes(termLimpo);

          case 'email':
            return cliente.email && cliente.email.toLowerCase().includes(searchValue);

          case 'telefone':
            // Para telefone, comparar apenas números
            const telLimpo = cliente.tel1 ? cliente.tel1.replace(/\D/g, '') : '';
            const searchTelLimpo = term.replace(/\D/g, '');
            return telLimpo && telLimpo.includes(searchTelLimpo);

          case 'nome':
          default:
            // Busca por nome (mais flexível)
            return cliente.nome && cliente.nome.toLowerCase().includes(searchValue);
        }
      });

      setSearchResults(filteredResults);

    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Manipular mudança no campo de busca
  const handleSearchChange = (value: string) => {
    const tipoBusca = detectarTipoBusca(value);

    // Formatar CPF/CNPJ se for documento
    if (tipoBusca === 'cpf' || tipoBusca === 'cnpj') {
      const documentoFormatado = formatarCPF(value);
      setSearchTerm(documentoFormatado);

      // Buscar quando tiver pelo menos 11 dígitos (CPF) ou 14 dígitos (CNPJ)
      const documentoLimpo = documentoFormatado.replace(/\D/g, '');
      if (documentoLimpo.length === 11 || documentoLimpo.length === 14) {
        // Para documentos completos, usar auto-preenchimento
        buscarPorCPF(documentoLimpo);
      } else if (documentoLimpo.length >= 3) {
        // Para documentos parciais, fazer busca geral
        searchClientes(documentoFormatado);
      }
    } else {
      // Para outros tipos, fazer busca direta
      setSearchTerm(value);
      if (value.length >= 3) {
        searchClientes(value);
      } else {
        setSearchResults([]);
      }
    }
  };

  // Selecionar cliente
  const handleClienteSelect = (cliente: Cliente) => {
    onClienteSelect(cliente);
    setShowResults(false);
    setSearchTerm('');
    limparDados();
  };

  // Limpar seleção
  const handleClearSelection = () => {
    onClienteSelect(null as any);
    setSearchTerm('');
    limparDados();
  };

  // Auto-preenchimento quando cliente for encontrado por CPF
  useEffect(() => {
    if (dadosCliente) {
      const cliente: Cliente = {
        id: dadosCliente.id,
        nome: dadosCliente.nome,
        cpf: dadosCliente.cpf,
        email: dadosCliente.email,
        tel1: dadosCliente.tel1
      };
      onClienteSelect(cliente);
      setSearchTerm('');
    }
  }, [dadosCliente, onClienteSelect]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Cliente selecionado */}
      {selectedCliente && (
        <Card
          variant="borderless"
          style={{
            borderRadius: 12,
            border: "1px solid #b7eb8f",
            background: "#f6ffed",
          }}
          styles={{ body: { padding: "16px 20px" } }}
        >
          <Flex justify="space-between" align="center">
            <Space size={12}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "#d9f7be",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleOutlined style={{ fontSize: 20, color: "#389e0d" }} />
              </div>
              <div>
                <Text strong style={{ color: "#135200" }}>{selectedCliente.nome}</Text>
                <br />
                <Text style={{ fontSize: 12, color: "#389e0d" }}>
                  {selectedCliente.cpf} • {selectedCliente.email}
                </Text>
              </div>
            </Space>
            <Button
              size="small"
              icon={<SwapOutlined />}
              onClick={handleClearSelection}
              style={{
                color: "#389e0d",
                borderColor: "#b7eb8f",
              }}
            >
              Alterar
            </Button>
          </Flex>
        </Card>
      )}

      {/* Busca de cliente */}
      {!selectedCliente && (
        <Card
          style={{ borderRadius: 12 }}
          title={
            <Space>
              <TeamOutlined />
              <span>Buscar Cliente</span>
            </Space>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Campo de busca inteligente */}
            <div style={{ position: "relative" }}>
              <Flex gap={8}>
                <Input
                  placeholder={dynamicPlaceholder}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => setShowResults(true)}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  suffix={
                    (isSearching || loadingAutoPreenchimento) ? (
                      <Spin size="small" />
                    ) : undefined
                  }
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  onClick={() => searchClientes(searchTerm)}
                  disabled={!searchTerm || isSearching || loadingAutoPreenchimento}
                >
                  Buscar
                </Button>
              </Flex>

              {/* Resultados da busca */}
              {showResults && searchResults.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 50,
                    width: "100%",
                    marginTop: 8,
                    background: "#fff",
                    border: "1px solid #d9d9d9",
                    borderRadius: 8,
                    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                    maxHeight: 240,
                    overflowY: "auto",
                  }}
                >
                  {searchResults.map((cliente) => (
                    <div
                      key={cliente.id}
                      onClick={() => handleClienteSelect(cliente)}
                      style={{
                        padding: 12,
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <Space size={12}>
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            background: "#e6f4ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <UserOutlined style={{ fontSize: 14, color: "#1677ff" }} />
                        </div>
                        <div>
                          <Text strong>{cliente.nome}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {cliente.cpf} • {cliente.email}
                          </Text>
                        </div>
                      </Space>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Alertas */}
            {erroAutoPreenchimento && (
              <Alert
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                message={erroAutoPreenchimento}
              />
            )}

            {searchResults.length === 0 && searchTerm.length >= 3 && !isSearching && (
              <Alert
                type="info"
                showIcon
                icon={<ExclamationCircleOutlined />}
                message={
                  <span>
                    Nenhum cliente encontrado.
                    <Button type="link" style={{ padding: 0, height: "auto", marginLeft: 4 }}>
                      Cadastrar novo cliente
                    </Button>
                  </span>
                }
              />
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
